import { memo, useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import SettingsBrightnessRoundedIcon from '@mui/icons-material/SettingsBrightnessRounded';
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { useColorMode } from '../../theme/ThemeProvider';
import { READING_WIDTHS, type ReadingWidth } from '../../config/defaults';
import type { ColorMode } from '../../theme/createAppTheme';
import BrandMark from '../brand/BrandMark';

export interface ViewerToolbarProps {
  title: string;
  subtitle?: string;
  /** Raw Markdown, used by the copy and download actions. */
  content: string;
  fileName?: string;
  tocOpen: boolean;
  tocAvailable: boolean;
  onToggleToc: () => void;
  searchOpen: boolean;
  searchAvailable: boolean;
  onToggleSearch: () => void;
  readingWidth: ReadingWidth;
  onReadingWidthChange: (width: ReadingWidth) => void;
  onOpenFile?: () => void;
  onGoHome?: () => void;
}

const MODE_OPTIONS: { value: ColorMode; label: string; icon: JSX.Element }[] = [
  { value: 'light', label: 'Light', icon: <LightModeRoundedIcon fontSize="small" /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeRoundedIcon fontSize="small" /> },
  { value: 'system', label: 'System', icon: <SettingsBrightnessRoundedIcon fontSize="small" /> },
];

const WIDTH_OPTIONS: { value: ReadingWidth; label: string }[] = [
  { value: 'narrow', label: 'Narrow' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'wide', label: 'Wide' },
  { value: 'full', label: 'Full width' },
];

const TOUCH_TOOLTIP_PROPS = {
  enterTouchDelay: 0,
  leaveTouchDelay: 1800,
} as const;

/** Sticky document toolbar. Every control maps to a real reader action. */
export const ViewerToolbar = memo(function ViewerToolbar({
  title,
  subtitle,
  content,
  fileName = 'document.md',
  tocOpen,
  tocAvailable,
  onToggleToc,
  searchOpen,
  searchAvailable,
  onToggleSearch,
  readingWidth,
  onReadingWidthChange,
  onOpenFile,
  onGoHome,
}: ViewerToolbarProps) {
  const { mode, resolvedMode, setMode } = useColorMode();
  const { copy, copied } = useCopyToClipboard();

  const [modeAnchor, setModeAnchor] = useState<HTMLElement | null>(null);
  const [widthAnchor, setWidthAnchor] = useState<HTMLElement | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);

  const download = () => {
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName.endsWith('.md') ? fileName : `${fileName}.md`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AppBar position="sticky" className="toolbar" component="header">
      <Toolbar sx={{ gap: { xs: 0.5, sm: 1.25 }, minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
        {tocAvailable && (
          <Tooltip title={tocOpen ? 'Hide contents' : 'Show contents'} {...TOUCH_TOOLTIP_PROPS}>
            <IconButton
              edge="start"
              aria-label="Toggle table of contents"
              aria-expanded={tocOpen}
              onClick={onToggleToc}
              color={tocOpen ? 'primary' : 'default'}
            >
              <MenuRoundedIcon />
            </IconButton>
          </Tooltip>
        )}

        <Box
          component="button"
          type="button"
          disabled={!onGoHome}
          aria-label="MarkGlass home"
          onClick={onGoHome}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.75, flex: 'none',
            p: 0.5, mr: { xs: 0, sm: 0.5 }, border: 0, background: 'none',
            color: 'primary.main', cursor: onGoHome ? 'pointer' : 'default',
          }}
        >
          <BrandMark size={22} />
          <Typography component="span" variant="subtitle2" sx={{ display: { xs: 'none', lg: 'inline' }, fontWeight: 750, color: 'text.primary', letterSpacing: '-0.03em', whiteSpace: 'nowrap' }}>
            MarkGlass
          </Typography>
        </Box>

        <Box sx={{ height: 25, width: '1px', bgcolor: 'divider', display: { xs: 'none', sm: 'block' }, mr: 0.5 }} />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontSize: { xs: '0.86rem', sm: '0.94rem' }, fontWeight: 700, letterSpacing: '-0.02em' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.68rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={0.25} alignItems="center">
          {onOpenFile && <>
            <Tooltip title="Open Markdown file" {...TOUCH_TOOLTIP_PROPS}>
              <IconButton aria-label="Open Markdown file" onClick={onOpenFile} sx={{ display: { xs: 'inline-flex', sm: 'none' }, bgcolor: 'primary.main', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.dark' } }}>
                <FolderOpenRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button variant="contained" size="small" startIcon={<FolderOpenRoundedIcon />} onClick={onOpenFile} sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap', mr: { sm: 0.5, md: 1 }, px: 1.5 }}>
              Open file
            </Button>
          </>}

          {searchAvailable && (
            <Tooltip title="Find in document (Ctrl+F)" {...TOUCH_TOOLTIP_PROPS}>
              <IconButton
                aria-label="Find in document"
                className="toolbar-search-trigger"
                aria-expanded={searchOpen}
                onClick={onToggleSearch}
                color={searchOpen ? 'primary' : 'default'}
              >
                <SearchRoundedIcon />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={copied ? 'Copied' : 'Copy Markdown'} {...TOUCH_TOOLTIP_PROPS}>
            <IconButton aria-label="Copy Markdown" onClick={() => void copy(content)} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              <Badge color="success" variant="dot" invisible={!copied}>
                {copied ? <CheckRoundedIcon color="success" /> : <ContentCopyRoundedIcon />}
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Download Markdown" {...TOUCH_TOOLTIP_PROPS}>
            <IconButton aria-label="Download Markdown" onClick={download} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              <DownloadRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Print" {...TOUCH_TOOLTIP_PROPS}>
            <IconButton
              aria-label="Print document"
              onClick={() => window.print()}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              <PrintRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reading width" {...TOUCH_TOOLTIP_PROPS}>
            <IconButton
              aria-label="Reading width"
              aria-haspopup="menu"
              onClick={(event) => setWidthAnchor(event.currentTarget)}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              <FormatAlignCenterRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Colour theme" {...TOUCH_TOOLTIP_PROPS}>
            <IconButton
              aria-label="Colour theme"
              aria-haspopup="menu"
              onClick={(event) => setModeAnchor(event.currentTarget)}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {resolvedMode === 'dark' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="More document actions" {...TOUCH_TOOLTIP_PROPS}>
            <IconButton aria-label="More document actions" aria-haspopup="menu" onClick={(event) => setMoreAnchor(event.currentTarget)} sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
              <MoreHorizRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>

      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        <MenuItem onClick={() => { void copy(content); setMoreAnchor(null); }}><ListItemIcon><ContentCopyRoundedIcon fontSize="small" /></ListItemIcon><ListItemText>Copy Markdown</ListItemText></MenuItem>
        <MenuItem onClick={() => { download(); setMoreAnchor(null); }}><ListItemIcon><DownloadRoundedIcon fontSize="small" /></ListItemIcon><ListItemText>Download Markdown</ListItemText></MenuItem>
        <MenuItem onClick={() => { window.print(); setMoreAnchor(null); }}><ListItemIcon><PrintRoundedIcon fontSize="small" /></ListItemIcon><ListItemText>Print</ListItemText></MenuItem>
        <MenuItem onClick={() => { setWidthAnchor(moreAnchor); setMoreAnchor(null); }}><ListItemIcon><FormatAlignCenterRoundedIcon fontSize="small" /></ListItemIcon><ListItemText>Reading width</ListItemText></MenuItem>
        <MenuItem onClick={() => { setModeAnchor(moreAnchor); setMoreAnchor(null); }}><ListItemIcon><LightModeRoundedIcon fontSize="small" /></ListItemIcon><ListItemText>Colour theme</ListItemText></MenuItem>
      </Menu>

      <Menu anchorEl={modeAnchor} open={Boolean(modeAnchor)} onClose={() => setModeAnchor(null)}>
        {MODE_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={mode === option.value}
            onClick={() => {
              setMode(option.value);
              setModeAnchor(null);
            }}
          >
            <ListItemIcon>{option.icon}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={widthAnchor} open={Boolean(widthAnchor)} onClose={() => setWidthAnchor(null)}>
        {WIDTH_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={readingWidth === option.value}
            onClick={() => {
              onReadingWidthChange(option.value);
              setWidthAnchor(null);
            }}
          >
            <ListItemText
              primary={option.label}
              secondary={
                READING_WIDTHS[option.value] === 0
                  ? 'Fill the window'
                  : `${READING_WIDTHS[option.value]} characters`
              }
            />
          </MenuItem>
        ))}
      </Menu>
    </AppBar>
  );
});

export default ViewerToolbar;
